"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    marketIndex: string;
    indexFund: string;
    activeFund: string;
    tracksClosely: string;
    divergesWidely: string;
    upside: string;
    downside: string;
    indexTitle: string;
    indexDescription: string;
    activeTitle: string;
    activeDescription: string;
  }
> = {
  ja: {
    marketIndex: "市場指数",
    indexFund: "インデックスファンド",
    activeFund: "アクティブファンド",
    tracksClosely: "ほぼ重なって動く → 差が小さい",
    divergesWidely: "大きく離れて動く → 上にも下にもブレやすい",
    upside: "上振れ",
    downside: "下振れ",
    indexTitle: "インデックス",
    indexDescription: "市場指数にほぼ沿う動きを目指す — 低コストで中身が分かりやすい",
    activeTitle: "アクティブ",
    activeDescription: "市場指数を上回ることを目指す — 上振れも下振れも出やすい",
  },
  en: {
    marketIndex: "Market index",
    indexFund: "Index fund",
    activeFund: "Active fund",
    tracksClosely: "Moves almost in step → small gap",
    divergesWidely: "Diverges widely → swings both above and below",
    upside: "Above",
    downside: "Below",
    indexTitle: "Index",
    indexDescription: "Aims to track the market index closely — low cost and easy to see what is inside",
    activeTitle: "Active",
    activeDescription: "Aims to beat the market index — more likely to swing either way",
  },
};

// ── Line data ──────────────────────────────────────────────────────

const N = 20;

// Smooth market line — gentle upward trend
const MARKET = [40, 42, 39, 43, 41, 44, 42, 46, 44, 48, 46, 50, 48, 52, 50, 54, 52, 56, 55, 58];

// Index fund: tracks market closely (±1.5)
const INDEX_FUND = MARKET.map(
  (v, i) =>
    v +
    [1.2, -0.8, 1.5, -1.0, 0.8, -1.2, 1.0, -1.5, 1.2, -0.8, 1.4, -1.0, 0.6, -1.3, 1.0, -1.2, 0.8, -0.6, 1.0, -0.5][i],
);

// Active fund: big swings, sometimes above, sometimes below
const ACTIVE_FUND = [40, 48, 34, 52, 36, 56, 32, 58, 40, 62, 44, 54, 34, 60, 48, 56, 42, 64, 52, 70];

// ── SVG helpers ────────────────────────────────────────────────────

function toPoints(series: number[], vw: number, vh: number, px: number, py: number, min: number, max: number) {
  return series.map((v, i) => ({
    x: px + (i / (series.length - 1)) * (vw - px * 2),
    y: py + ((max - v) / (max - min)) * (vh - py * 2),
  }));
}

function smoothPath(pts: { x: number; y: number }[]) {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
  }
  return d;
}

// ── Shared constants (larger) ──────────────────────────────────────

const VW = 380;
const VH = 240;
const PX = 24;
const PY = 32;
const ALL = [...MARKET, ...INDEX_FUND, ...ACTIVE_FUND];
const MIN = Math.min(...ALL) - 6;
const MAX = Math.max(...ALL) + 6;

// ── Index chart ────────────────────────────────────────────────────

function IndexChart() {
  const t = STRINGS[useDocLocale()];
  const mPts = toPoints(MARKET, VW, VH, PX, PY, MIN, MAX);
  const fPts = toPoints(INDEX_FUND, VW, VH, PX, PY, MIN, MAX);
  const marketD = smoothPath(mPts);
  const fundD = smoothPath(fPts);

  const bandD = `${smoothPath(fPts)} L ${mPts[mPts.length - 1].x} ${mPts[mPts.length - 1].y} ${smoothPath([...mPts].reverse()).replace("M", "L")} Z`;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      {/* Grid */}
      {[0.25, 0.5, 0.75].map((f) => {
        const y = PY + f * (VH - PY * 2);
        return (
          <line key={f} x1={PX} y1={y} x2={VW - PX} y2={y} stroke="currentColor" strokeDasharray="3 6" strokeOpacity={0.08} />
        );
      })}

      {/* Band fill */}
      <path d={bandD} fill={COLOR.sky} fillOpacity={0.1} />

      {/* Market line */}
      <path d={marketD} fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 4" strokeOpacity={0.25} strokeLinecap="round" />

      {/* Fund line */}
      <path d={fundD} fill="none" stroke={COLOR.sky} strokeWidth="3" strokeLinecap="round" />

      {/* End dots */}
      <circle cx={mPts[N - 1].x} cy={mPts[N - 1].y} r={4} className="fill-white dark:fill-zinc-900" stroke="currentColor" strokeWidth="1.2" strokeOpacity={0.3} />
      <circle cx={fPts[N - 1].x} cy={fPts[N - 1].y} r={4} className="fill-white dark:fill-zinc-900" stroke={COLOR.sky} strokeWidth="1.8" />

      {/* Legend */}
      <line x1={PX} y1={14} x2={PX + 20} y2={14} stroke="currentColor" strokeWidth="2" strokeDasharray="5 3" strokeOpacity={0.3} strokeLinecap="round" />
      <text x={PX + 26} y={17} fontSize="9" fill="currentColor" fontWeight="500">{t.marketIndex}</text>
      <line x1={PX + 100} y1={14} x2={PX + 120} y2={14} stroke={COLOR.sky} strokeWidth="3" strokeLinecap="round" />
      <text x={PX + 126} y={17} fontSize="9" fill={COLOR.sky} fontWeight="600">{t.indexFund}</text>

      {/* Annotation */}
      <text x={VW / 2} y={VH - 8} textAnchor="middle" fontSize="10" fill={COLOR.sky} fontWeight="600">
        {t.tracksClosely}
      </text>
    </svg>
  );
}

// ── Active chart ───────────────────────────────────────────────────

function ActiveChart() {
  const t = STRINGS[useDocLocale()];
  const mPts = toPoints(MARKET, VW, VH, PX, PY, MIN, MAX);
  const aPts = toPoints(ACTIVE_FUND, VW, VH, PX, PY, MIN, MAX);
  const marketD = smoothPath(mPts);
  const activeD = smoothPath(aPts);

  const gapFill = `${smoothPath(aPts)} L ${mPts[mPts.length - 1].x} ${mPts[mPts.length - 1].y} ${smoothPath([...mPts].reverse()).replace("M", "L")} Z`;

  // Find biggest above/below market points
  let aboveIdx = 0;
  let belowIdx = 0;
  ACTIVE_FUND.forEach((v, i) => {
    if (v - MARKET[i] > ACTIVE_FUND[aboveIdx] - MARKET[aboveIdx]) aboveIdx = i;
    if (MARKET[i] - v > MARKET[belowIdx] - ACTIVE_FUND[belowIdx]) belowIdx = i;
  });

  function gapAnnotation(idx: number, label: string, side: "above" | "below") {
    const ax = mPts[idx].x;
    const my = mPts[idx].y;
    const ay = aPts[idx].y;
    const labelY = side === "above" ? ay - 8 : ay + 13;
    return (
      <g key={label}>
        <line x1={ax} y1={my} x2={ax} y2={ay} stroke={COLOR.amber} strokeWidth="1.2" strokeDasharray="2 2" />
        <circle cx={ax} cy={my} r={2.5} fill="currentColor" fillOpacity={0.2} />
        <circle cx={ax} cy={ay} r={3} fill={COLOR.amber} />
        <text x={ax} y={labelY} textAnchor="middle" fontSize="8.5" fill={COLOR.amber} fontWeight="700">
          {label}
        </text>
      </g>
    );
  }

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full">
      {/* Grid */}
      {[0.25, 0.5, 0.75].map((f) => {
        const y = PY + f * (VH - PY * 2);
        return (
          <line key={f} x1={PX} y1={y} x2={VW - PX} y2={y} stroke="currentColor" strokeDasharray="3 6" strokeOpacity={0.08} />
        );
      })}

      {/* Gap fill */}
      <path d={gapFill} fill={COLOR.amber} fillOpacity={0.08} />

      {/* Market line */}
      <path d={marketD} fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 4" strokeOpacity={0.25} strokeLinecap="round" />

      {/* Active line */}
      <path d={activeD} fill="none" stroke={COLOR.amber} strokeWidth="3" strokeLinecap="round" />

      {/* Gap annotations */}
      {gapAnnotation(aboveIdx, t.upside, "above")}
      {gapAnnotation(belowIdx, t.downside, "below")}

      {/* End dots */}
      <circle cx={mPts[N - 1].x} cy={mPts[N - 1].y} r={4} className="fill-white dark:fill-zinc-900" stroke="currentColor" strokeWidth="1.2" strokeOpacity={0.3} />
      <circle cx={aPts[N - 1].x} cy={aPts[N - 1].y} r={4} className="fill-white dark:fill-zinc-900" stroke={COLOR.amber} strokeWidth="1.8" />

      {/* Legend */}
      <line x1={PX} y1={14} x2={PX + 20} y2={14} stroke="currentColor" strokeWidth="2" strokeDasharray="5 3" strokeOpacity={0.3} strokeLinecap="round" />
      <text x={PX + 26} y={17} fontSize="9" fill="currentColor" fontWeight="500">{t.marketIndex}</text>
      <line x1={PX + 100} y1={14} x2={PX + 120} y2={14} stroke={COLOR.amber} strokeWidth="3" strokeLinecap="round" />
      <text x={PX + 126} y={17} fontSize="9" fill={COLOR.amber} fontWeight="600">{t.activeFund}</text>

      {/* Annotation */}
      <text x={VW / 2} y={VH - 8} textAnchor="middle" fontSize="10" fill={COLOR.amber} fontWeight="600">
        {t.divergesWidely}
      </text>
    </svg>
  );
}

// ── Export ──────────────────────────────────────────────────────────

export function DocFundStrategyDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <div className="grid gap-3 xl:grid-cols-2">
      <IllustrationCard
        title={t.indexTitle}
        description={t.indexDescription}
      >
        <IndexChart />
      </IllustrationCard>
      <IllustrationCard title={t.activeTitle} description={t.activeDescription}>
        <ActiveChart />
      </IllustrationCard>
    </div>
  );
}
